/**
 * Persist AI plan blobs on consumer trips — local JSON + Device Sync file id.
 */

import type { AggregatedPlanData } from '../utils/aggregatedPlanData';
import { parseAggregatedPlanData } from '../utils/aggregatedPlanData';
import { downloadFile, uploadDeviceSyncFile } from './filesService';
import { getTrip, getUserIdForTripWrites, updateTrip } from './tripsService';
import type { Trip, TripCityVisitMeta } from '../types/client';

function emptyMeta(): TripCityVisitMeta {
  return {
    arrivalDate: null,
    departureDate: null,
    portArrivalHm: null,
    portDepartureHm: null,
    cruisePortStatus: null,
  };
}

/** Clear local plan blob + job metadata for a city visit (re-enqueue path). */
export function clearCityPlanData(trip: Trip, cityVisitId: string): Partial<Trip> {
  const meta = { ...(trip.cityVisitMeta ?? {}) };
  const prev = meta[cityVisitId] ?? emptyMeta();
  meta[cityVisitId] = {
    ...prev,
    aiJobId: null,
    aiJobStartedAt: null,
    aiStatus: null,
    aiUpdatedAt: new Date().toISOString(),
    aiGeneratedPlanDataFileId: null,
  };
  const aiPlanDataByCity = { ...(trip.aiPlanDataByCity ?? {}) };
  delete aiPlanDataByCity[cityVisitId];
  return { cityVisitMeta: meta, aiPlanDataByCity };
}

export function getStoredPlanData(
  trip: Trip,
  cityVisitId: string
): AggregatedPlanData | null {
  const stored = trip.aiPlanDataByCity?.[cityVisitId];
  if (!stored) return null;
  return parseAggregatedPlanData(stored);
}

/**
 * Persist parsed plan locally and optionally upload to Device Sync files.
 * Updates aiStatus to done and stores aiGeneratedPlanDataFileId on meta.
 */
export async function persistCityPlanData(options: {
  tripId: string;
  cityVisitId: string;
  plan: AggregatedPlanData;
  uploadFile?: boolean;
}): Promise<Trip | null> {
  const userId = getUserIdForTripWrites();
  if (!userId) return null;
  const trip = getTrip(userId, options.tripId);
  if (!trip) return null;

  const now = new Date().toISOString();
  let fileId: string | null =
    trip.cityVisitMeta?.[options.cityVisitId]?.aiGeneratedPlanDataFileId ?? null;

  if (options.uploadFile !== false) {
    try {
      const json = JSON.stringify(options.plan);
      const blob = new Blob([json], { type: 'application/json' });
      fileId = await uploadDeviceSyncFile(
        blob,
        'application/json',
        `ai-plan-${options.cityVisitId}.json`
      );
    } catch (err) {
      console.warn('persistCityPlanData: file upload failed — keeping local only', err);
    }
  }

  const prev = trip.cityVisitMeta?.[options.cityVisitId] ?? emptyMeta();
  const nextMeta: TripCityVisitMeta = {
    ...prev,
    aiStatus: 'done',
    aiUpdatedAt: now,
    aiGeneratedPlanDataFileId: fileId,
  };

  return updateTrip(userId, options.tripId, {
    cityVisitMeta: {
      ...(trip.cityVisitMeta ?? {}),
      [options.cityVisitId]: nextMeta,
    },
    aiPlanDataByCity: {
      ...(trip.aiPlanDataByCity ?? {}),
      [options.cityVisitId]: options.plan,
    },
  });
}

/** Update aiStatus while polling (queued / running / failed). */
export function updateCityAiStatus(
  tripId: string,
  cityVisitId: string,
  aiStatus: string
): Trip | null {
  const userId = getUserIdForTripWrites();
  if (!userId) return null;
  const trip = getTrip(userId, tripId);
  if (!trip) return null;
  const prev = trip.cityVisitMeta?.[cityVisitId] ?? emptyMeta();
  return updateTrip(userId, tripId, {
    cityVisitMeta: {
      ...(trip.cityVisitMeta ?? {}),
      [cityVisitId]: {
        ...prev,
        aiStatus,
        aiUpdatedAt: new Date().toISOString(),
      },
    },
  });
}

/**
 * If meta has a file id but no local plan JSON, download and hydrate.
 */
export async function hydrateCityPlanFromFile(
  tripId: string,
  cityVisitId: string
): Promise<AggregatedPlanData | null> {
  const userId = getUserIdForTripWrites();
  if (!userId) return null;
  const trip = getTrip(userId, tripId);
  if (!trip) return null;

  const existing = getStoredPlanData(trip, cityVisitId);
  if (existing) return existing;

  const fileId = trip.cityVisitMeta?.[cityVisitId]?.aiGeneratedPlanDataFileId?.trim();
  if (!fileId) return null;

  try {
    const blob = await downloadFile(fileId);
    const text = await blob.text();
    const plan = parseAggregatedPlanData(text);
    if (!plan) return null;
    await persistCityPlanData({
      tripId,
      cityVisitId,
      plan,
      uploadFile: false,
    });
    return plan;
  } catch (err) {
    console.warn('hydrateCityPlanFromFile failed', err);
    return null;
  }
}

/** largeCityBlobs fragment for Device Sync city visit encode. */
export function planFileIdBlob(
  trip: Trip,
  cityVisitId: string
): { aiGeneratedPlanDataFileId: string } | null {
  const fileId = trip.cityVisitMeta?.[cityVisitId]?.aiGeneratedPlanDataFileId?.trim();
  if (!fileId) return null;
  return { aiGeneratedPlanDataFileId: fileId };
}
